/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: photov2 专用下载按钮（基于 photosByContentSlice 与 photoFilterSlice）
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 */
import React, { useState, useCallback, useMemo } from 'react'
import { Button, Popover, Modal, message } from 'antd'
import { ExclamationCircleFilled } from '@ant-design/icons'
import {
  useAppDispatch,
  useAppSelector,
  usePayment
} from '../../../../app/hooks'
import {
  sensorsClassification,
  sensorsNamingMethod,
  formatTitlesData
} from '../../lib/photo'
import {
  exportPhotos,
  photoTitles,
  setExportListDrawerVisible,
  buildParamsWithAuthPolicy,
  setClientDownloadDrawerVisible,
  setClientDownloadTasks
} from '../../../../actions/photo'
import CONFIG from '../../../../constants/config'
import sensors, { sensorsRole } from '../../../../utils/sensors'
import { capturePromiseException } from '../../../../utils/sentryUtils'
import export_photo_tip_img from '../../../../images/export_photo_tip.png'
import vipLimitImg from '../../../../images/vip-limit.png'
import FolderModal from '../../../components/FolderModal'
import MultiFolderModal from '../../../components/MultiFolderModel'
import { setLoading } from '../../../../actions/main'
import desktopLib from '../../../../utils/desktopLib'
import { markClientPromoPending } from '../../../../utils/downloadClientPromo'
import {
  supportsNewDownloadFeature,
  isVersionBetween
} from '../../../../utils/versionUtils'
import { selectSearchFilter } from '../../../../slices/photoFilterSlice'
import { selectPreciseSearch } from '../../../../slices/photosByPreciseSlice'
import { trackPicsPageClickV2 } from '../../utils/track'
import {
  buildFetchPhotosParamsWithOptions,
  calcPickedMemberNum
} from '../../lib/photo'
import {
  getSelectionConfig,
  normalizeToSet,
  PhotoSelectionSource,
  useActiveSelection,
  buildSuggestedPhotosQueryFilter
} from '../../utils/selectionHelpers'

interface DownloadButtonProps {
  dataSource?: PhotoSelectionSource
}

/**
 * 下载按钮组件（photov2）
 */
const DownloadButton: React.FC<DownloadButtonProps> = ({
  dataSource = 'content'
}) => {
  const [folderModalVisible, setFolderModalVisible] = useState(false)
  const [folderTitleList, setFolderTitleList] = useState<any[]>([])
  const [photoTitleList, setPhotoTitleList] = useState<any[]>([])
  const dispatch = useAppDispatch()

  const queryFilter = useAppSelector(selectSearchFilter)
  const listIsAscSort = useAppSelector((state) => {
    if (dataSource === 'precise') {
      return state.photosByPrecise.isAscSort
    }
    return state.photosByContent.isAscSort
  })
  const preciseSearch = useAppSelector(selectPreciseSearch)
  const groupID = useAppSelector((state) => state.group.groupId)
  const groupName = useAppSelector((state) => state.group.groupName)
  const members = useAppSelector((state) => state.group.members)
  const myRole = useAppSelector((state) => state.group.myRole)
  const isVip = useAppSelector((state) => state.group.isVip)
  const vipKind = useAppSelector((state) => state.group.vipInfo?.vip_kind)
  const payment = usePayment()
  const currentState = useAppSelector((state) => state)

  const {
    selectionConfig,
    hasSuggestedSelected,
    activePhotos: photos,
    activePhotosNum: photosNum,
    activeCheckAll: checkAll,
    activeCheckedPhotos: checkedPhotos,
    activeUnCheckedPhotos: unCheckedPhotos,
    suggestedWords,
    suggestedQueryFilter
  } = useActiveSelection(dataSource)

  const existingClientTasks = useAppSelector(
    (state) => state.photo.clientDownloadTasks
  )

  // 计算是否禁用下载按钮
  const disabled = useMemo(() => {
    return checkedPhotos.size === 0 && !checkAll
  }, [checkedPhotos, checkAll])

  // 计算选中的照片数量
  const checkedNum = useMemo(() => {
    if (checkAll) {
      return photosNum - unCheckedPhotos.size
    }
    return checkedPhotos.size
  }, [checkAll, photosNum, checkedPhotos, unCheckedPhotos])

  // 当前已加载且本次勾选中的照片，是否包含「已下载」标签（未加载页不参与判断）
  const hasDownloadedTagInCurrentPhotos = useMemo(() => {
    const selectedLoaded = checkAll
      ? photos.filter((photo: any) => !unCheckedPhotos.has(photo.seq))
      : photos.filter((photo: any) => checkedPhotos.has(photo.seq))
    return selectedLoaded.some((photo: any) => Boolean(photo?.isDownloaded))
  }, [photos, checkAll, checkedPhotos, unCheckedPhotos])

  const groupVipLevel = useMemo(() => {
    if (!isVip) return 'noVip'
    if (vipKind === CONFIG.VIP_KIND.VIP) return 'vip'
    if (vipKind === CONFIG.VIP_KIND.VVIP) return 'vvip'
    if (vipKind === CONFIG.VIP_KIND.SVIP) return 'svip'
    if (vipKind === CONFIG.VIP_KIND.EVIP) return 'evip'
    return 'vip'
  }, [isVip, vipKind])

  // 计算排除的照片（全选时）
  const exclude: string[] = useMemo(() => {
    if (checkAll) {
      return photos
        .filter((photo) => unCheckedPhotos.has(photo.seq))
        .map((photo) => photo.file)
    }
    return []
  }, [checkAll, photos, unCheckedPhotos])

  // 计算包含的照片（非全选时）
  const include: string[] = useMemo(() => {
    if (!checkAll) {
      return photos
        .filter((photo) => checkedPhotos.has(photo.seq))
        .map((photo) => photo.file)
    }
    return []
  }, [checkAll, photos, checkedPhotos])

  // 第一张被选中的照片（用于判断是否因为存储超限而无法按张下载）
  const firstSelectedPhoto = useMemo(() => {
    if (photos.length === 0) return undefined
    if (checkAll) {
      return photos.find((photo) => !unCheckedPhotos.has(photo.seq))
    }
    return photos.find((photo) => checkedPhotos.has(photo.seq))
  }, [photos, checkAll, checkedPhotos, unCheckedPhotos])

  const firstSelectedPhotoIsLimit =
    !!firstSelectedPhoto?.isLimitViewByPhotoCount

  // 取消全选（photov2 使用 photosByContentSlice 的 clearCheck，支持清除推荐区选项）
  const cancelCheckAll = useCallback(() => {
    if (hasSuggestedSelected) {
      dispatch({ type: 'photosByContent/clearSuggestedCheck' })
    } else {
      dispatch(selectionConfig.clearCheckAction())
    }
  }, [dispatch, selectionConfig, hasSuggestedSelected])

  // 下载埋点函数
  const trackDownloadClick = useCallback(
    (
      clickItem: string,
      folderRule?: string,
      namingRule?: string | string[],
      bool1?: boolean,
      folderTitles?: string[],
      trackParams?: Record<string, unknown>
    ) => {
      const classification = folderRule
        ? sensorsClassification(folderRule)
        : undefined

      const extraFields: any = {}

      // classification='date' 时，传递 timeline 字段（开始时间~结束时间）
      if (
        classification === 'date' &&
        queryFilter?.times?.[0] &&
        queryFilter?.times?.[1]
      ) {
        const startTime = queryFilter.times[0].format(CONFIG.timeFormat)
        const endTime = queryFilter.times[1].format(CONFIG.timeFormat)
        extraFields.timeline = `${startTime}~${endTime}`
      }

      // classification='member' 时，传递 list1（用户ID列表）和 list2（部门ID列表）
      if (classification === 'member') {
        const userSet = new Set<string>()
        const deptSet = new Set<string>()

        const hasAllDepartmentSelected = queryFilter?.departments?.some(
          (dept) => dept?.id === '0'
        )

        if (hasAllDepartmentSelected) {
          // 全部成员时，按照需求传递字符串 "0"
          extraFields.list1 = ['0']
          extraFields.list2 = ['0']
        } else {
          if (queryFilter?.departmentUsers?.length) {
            queryFilter.departmentUsers.forEach((user) => {
              if (user.userID) {
                userSet.add(user.userID)
              }
            })
          }

          if (queryFilter?.departments?.length) {
            queryFilter.departments.forEach((dept) => {
              if (dept.id && dept.id !== '0') {
                deptSet.add(dept.id)
              }
            })
          }

          // 如果没有勾选任何成员/部门，也传递 "0"
          if (userSet.size === 0 && deptSet.size === 0) {
            extraFields.list1 = ['0']
            extraFields.list2 = ['0']
          } else {
            extraFields.list1 = Array.from(userSet)
            extraFields.list2 = Array.from(deptSet)
          }
        }
      }

      // classification='custom' 时，传递 labelName 字段（条目名称的具体值）
      if (
        classification === 'custom' &&
        folderTitles &&
        folderTitleList.length > 0
      ) {
        // 确保 folderTitles 是数组
        const titlesArray = Array.isArray(folderTitles)
          ? folderTitles
          : [folderTitles]

        // 从 folderTitleList 中找到对应的 titleName
        const labelNames = titlesArray
          .filter((titleId) => titleId && titleId !== 'watermark') // 排除 watermark
          .map((titleId) => {
            // 支持 titleId 匹配（完全匹配或包含匹配）
            const titleItem = folderTitleList.find((item) => {
              if (!item.titleId) return false
              // 如果 titleId 包含逗号，说明是多个ID合并的
              const itemIds = item.titleId.split(',')
              return itemIds.includes(titleId) || item.titleId === titleId
            })
            return titleItem?.titleName || ''
          })
          .filter((name) => name !== '')

        if (labelNames.length > 0) {
          extraFields.labelName = Array.from(new Set(labelNames)).join(',')
        }
      }
      if (classification === 'custom' && folderTitles) {
        if (folderTitles.join('') === 'watermark') {
          extraFields.labelName = '水印名称'
        }
      }

      sensors.track('web_click_page_workgroup_pic_download', {
        clickItem,
        ...(classification && { classification }),
        ...(namingRule && {
          namingMethod: sensorsNamingMethod(
            Array.isArray(namingRule) ? namingRule : [namingRule]
          )
        }),
        role: sensorsRole(myRole),
        groupID,
        ...(bool1 !== undefined && { bool1 }),
        bool2: hasDownloadedTagInCurrentPhotos,
        isVip,
        photoNum: checkedNum,
        fromPlace: 'watermarkContent',
        ...extraFields,
        track_sign:
          'aworkgroup.b4347.action.web_click_page_workgroup_pic_download',
        requirement_id: 607,
        ...trackParams
      })
    },
    [
      myRole,
      groupID,
      isVip,
      checkedNum,
      queryFilter,
      folderTitleList,
      hasDownloadedTagInCurrentPhotos
    ]
  )

  // 构建导出/下载的基础参数（基于 photov2 的 FilterState）
  const buildBaseExportFilter = useCallback(
    (purpose: 'folder' | 'file') => {
      if (!queryFilter || !groupID) return null

      let currentQueryFilter = queryFilter
      if (hasSuggestedSelected) {
        currentQueryFilter =
          buildSuggestedPhotosQueryFilter(
            queryFilter,
            suggestedQueryFilter,
            suggestedWords || []
          ) || queryFilter
      }

      const isPreciseSearch = dataSource === 'precise'
      const exportFilter: any = buildFetchPhotosParamsWithOptions({
        queryFilter: currentQueryFilter,
        groupID,
        lastPageCond: '',
        isAscSort: listIsAscSort,
        isPreciseSearch,
        excludeKeywords: isPreciseSearch ? preciseSearch?.notWords : undefined
      })

      if (checkAll) {
        exportFilter.filter.exclude = exclude
      } else {
        exportFilter.filter.include = include
      }

      exportFilter.purpose = purpose
      return exportFilter
    },
    [
      queryFilter,
      groupID,
      checkAll,
      exclude,
      include,
      hasSuggestedSelected,
      suggestedWords,
      suggestedQueryFilter,
      dataSource,
      listIsAscSort,
      preciseSearch
    ]
  )

  // 获取标题列表
  const getTitlesClick = useCallback(
    async (type: 'folder' | 'file') => {
      const baseFilter = buildBaseExportFilter(type)
      if (!baseFilter) return
      return dispatch(photoTitles(baseFilter))
    },
    [buildBaseExportFilter, dispatch]
  )

  // 下载照片点击处理（弹出文件夹命名弹窗）
  const onDownLoadPictureClick = async () => {
    if (!queryFilter || !groupID) return
    // 如果首张选中照片标记为存储超限，则提示并阻止弹窗
    if (firstSelectedPhotoIsLimit) {
      message.warning('团队存储已满，超限照片无法下载')
    }
    const filterForMember = queryFilter
    const membersNum = calcPickedMemberNum(filterForMember, members)

    const properties = {
      memberNum: membersNum,
      allMemberNum: members.length,
      photosNum: checkedNum,
      bool2: hasDownloadedTagInCurrentPhotos,
      start_date: filterForMember.times?.[0]?.format(CONFIG.timeFormat),
      end_date: filterForMember.times?.[1]?.format(CONFIG.timeFormat),
      groupID,
      isVip,
      fromPlace: 'watermarkContent'
    }
    trackPicsPageClickV2({ clickItem: 'download' })
    sensors.track('web_workgroup_picspage_click_download', properties)

    if (checkedNum > CONFIG.maxDownloadLimit) {
      sensors.track(
        'web_show_pop_workgroup_download_delete_photo_limit_exceeded',
        {
          operationType: 'download',
          photoNum: checkedNum,
          groupID,
          role: sensorsRole(myRole)
        }
      )
      Modal.info({
        title: `团队会员最多可支持${CONFIG.maxDownloadLimit}张照片的下载`,
        content: (
          <div>
            <p>请您适当的减少照片张数后下载</p>
          </div>
        ),
        okText: '我知道了',
        onOk: cancelCheckAll
      })
      return
    }

    // 仅基础会员（VIP）：2000张<选中照片数<=20000张，弹出升级引导；高级/超级/企业会员不拦截
    if (
      isVip &&
      vipKind === CONFIG.VIP_KIND.VIP &&
      checkedNum > CONFIG.directDownloadLimit
    ) {
      const trackDownloadLimitModal = (
        actionType: '出现弹窗' | '我知道了' | '升级会员'
      ) => {
        sensors.track('workgroup_b8069_completed', {
          requirement_id: 1029,
          track_sign: 'aworkgroup.b8069.completed.workgroup_b8069_completed',
          actionType,
          source_detail: 'downloadPhotoLimit2000',
          bool2: hasDownloadedTagInCurrentPhotos,
          photoNum: checkedNum,
          role: sensorsRole(myRole),
          groupID,
          groupVipLevel
        })
      }
      const recommendedVipKind =
        checkedNum <= 10000 ? CONFIG.VIP_KIND.VVIP : CONFIG.VIP_KIND.SVIP
      const recommendedLevel =
        recommendedVipKind === CONFIG.VIP_KIND.VVIP ? '高级会员' : '超级会员'

      trackDownloadLimitModal('出现弹窗')
      Modal.confirm({
        width: 520,
        icon: (
          <ExclamationCircleFilled style={{ color: '#faad14', fontSize: 22 }} />
        ),
        title: '基础会员最多可支持一次性下载2000张照片',
        content: (
          <div>
            <p
              style={{
                marginTop: 0,
                marginBottom: 16,
                color: 'rgba(0, 0, 0, 0.65)',
                fontSize: 14,
                lineHeight: 1.5
              }}
            >
              请您适当减少照片张数后下载，或升级{recommendedLevel}
            </p>
            {/* 切图左侧有约 19px 留白，向左偏移以与上方文案左对齐 */}
            <img
              src={vipLimitImg}
              alt=""
              style={{
                display: 'block',
                width: 'calc(100% + 19px)',
                maxWidth: 459,
                marginLeft: -19
              }}
            />
          </div>
        ),
        okText: '升级会员',
        cancelText: '取消',
        onCancel: () => {
          trackDownloadLimitModal('我知道了')
        },
        onOk: () => {
          trackDownloadLimitModal('升级会员')
          payment.openVipByKind(recommendedVipKind, 'downloadPhotoLimit2000')
        }
      })
      return
    }

    dispatch(setLoading(true))
    try {
      let folderData: any = await getTitlesClick('folder')
      folderData = formatTitlesData(folderData.titles)
      let photoData: any = await getTitlesClick('file')
      photoData = formatTitlesData(photoData.titles)
      setFolderTitleList(folderData)
      setPhotoTitleList(photoData)
      dispatch(setLoading(false))
      setFolderModalVisible(true)
    } catch (err) {
      dispatch(setLoading(false))
      capturePromiseException(err as any)
    }
  }

  // 客户端下载处理
  const onClientDownload = useCallback(
    async (
      folderRule: string,
      namingRule: string[],
      folderTitles: string[],
      clickItem = 'download',
      trackParams = {},
      folderV2?: string[]
    ) => {
      if (!navigator.onLine) {
        message.error('网络连接异常，无法创建下载任务')
        return
      }

      const supportsNewFeature = supportsNewDownloadFeature()
      if (!supportsNewFeature) {
        setFolderModalVisible(false)
        if (clickItem === 'downloadOnce') {
          onDownloadOnceWeb(folderRule, namingRule, folderTitles, folderV2)
        } else {
          onExportWeb(
            folderRule,
            namingRule,
            folderTitles,
            clickItem,
            trackParams,
            folderV2
          )
        }
        return
      }

      try {
        const currentTasks = existingClientTasks || []
        let hasRunningTask = currentTasks.some(
          (task: any) => task.status === 'downloading'
        )

        if (hasRunningTask) {
          // 如果前端显示有任务在下载，再次向客户端确认状态
          if (groupID) {
            try {
              // 调用 ClientDownloadDrawer 使用的接口，获取最新数据
              const result = await desktopLib.downloadGetTasks(groupID)
              console.log('result', result)
              if (result.success && result.data) {
                // 1. 用真实数据更新 Redux
                dispatch(setClientDownloadTasks(result.data))

                // 2. 重新检查真实状态
                const latestTasks = result.data || []
                hasRunningTask = latestTasks.some(
                  (task: any) => task.status === 'downloading'
                )

                if (hasRunningTask) {
                  message.warning(
                    '当前有任务正在下载中，请等待当前任务完成后再进行下载'
                  )
                  return
                }
              }
            } catch (e) {
              console.error('查询任务状态失败', e)
              message.error('检查任务状态失败，请稍后重试')
              return
            }
          }
        }

        trackDownloadClick(
          clickItem,
          folderRule,
          namingRule,
          true,
          folderTitles
        )

        let exportFilter: any = buildBaseExportFilter('file')
        if (!exportFilter) return

        // 处理文件夹规则
        if (folderRule !== 'customize') {
          folderTitles = []
        } else if (folderTitles.join('') === 'watermark') {
          folderRule = 'watermark'
          folderTitles = []
        } else {
          folderRule = ''
        }

        exportFilter.namePolicy = {
          file: namingRule,
          folder: folderRule,
          folderTitles
        }
        if (folderV2) {
          exportFilter.namePolicy = exportFilter.namePolicy || {}
          exportFilter.namePolicy.folderV2 = folderV2
        }

        exportFilter = buildParamsWithAuthPolicy(currentState, exportFilter)
        exportFilter.groupName = groupName

        const orderNo = localStorage.getItem('orderNo')
        if (clickItem === 'downloadOnce' && orderNo) {
          exportFilter.extra = {
            orderNo
          }
        }

        const result = await desktopLib.downloadCreateTask({
          downloadType: 'photos',
          exportFilter,
          clickItem
        })

        if (result.success) {
          setFolderModalVisible(false)

          const clientTask = {
            id: result.data.id,
            name: `照片下载任务_${new Date().toLocaleString()}`,
            status: 'downloading',
            progress: 0,
            downloadedCount: 0,
            totalCount: checkedNum,
            savePath: result.data.savePath,
            createdAt: new Date().toISOString()
          }

          const currentTasks2 = existingClientTasks || []
          const updatedTasks = [...currentTasks2, clientTask]

          const sevenDaysAgo = new Date()
          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

          const recentTasks = updatedTasks.filter((task) => {
            return new Date(task.createdAt) >= sevenDaysAgo
          })

          dispatch(setClientDownloadTasks(recentTasks))
          dispatch(setClientDownloadDrawerVisible(true))
          cancelCheckAll()
        } else {
          setFolderModalVisible(false)
          const errorMessage = result.error || ''
          if (errorMessage.includes('选择时间段内无照片')) {
            Modal.warning({
              title: '提示',
              content: '选择时间段内无照片'
            })
          } else {
            throw new Error(result.error || '客户端下载失败')
          }
        }
      } catch (error) {
        console.error('客户端下载失败:', error)
        message.error('客户端下载失败，请检查网络连接或稍后重试')
      }
    },
    [
      existingClientTasks,
      trackDownloadClick,
      buildBaseExportFilter,
      currentState,
      groupName,
      checkedNum,
      cancelCheckAll
    ]
  )

  // Web 端一次性下载（付费）
  const onDownloadOnceWeb = useCallback(
    (
      folderRule: string,
      namingRule: string[],
      folderTitles: string[],
      folderV2?: string[]
    ) => {
      setFolderModalVisible(false)
      let exportFilter: any = buildBaseExportFilter('file')
      if (!exportFilter) return

      if (folderRule !== 'customize') {
        folderTitles = []
      } else if (folderTitles.join('') === 'watermark') {
        folderRule = 'watermark'
        folderTitles = []
      } else {
        folderRule = ''
      }

      exportFilter.namePolicy = {
        file: namingRule,
        folder: folderRule,
        folderTitles
      }
      if (folderV2) {
        exportFilter.namePolicy = exportFilter.namePolicy || {}
        exportFilter.namePolicy.folderV2 = folderV2
      }
      exportFilter = buildParamsWithAuthPolicy(currentState, exportFilter)
      dispatch({
        type: 'SET_EXPORT_DOWNLOAD_PHOTO_ONCE_PARAMS',
        data: JSON.stringify(exportFilter)
      })
      payment.open('downloadOnce')
    },
    [buildBaseExportFilter, currentState, dispatch, payment]
  )

  // 单次付费批量下载照片（photov2 统一走 Web 逻辑）
  const onDownloadOnce = useCallback(
    (
      folderRule: string,
      namingRule: string[],
      folderTitles: string[],
      folderV2?: string[]
    ) => {
      onDownloadOnceWeb(folderRule, namingRule, folderTitles, folderV2)
    },
    [onDownloadOnceWeb]
  )

  // Web 导出逻辑
  const onExportWeb = useCallback(
    (
      folderRule: string,
      namingRule: string[],
      folderTitles: string[],
      clickItem = 'download',
      trackParams = {},
      folderV2?: string[]
    ) => {
      setFolderModalVisible(false)
      dispatch(setLoading(true))
      const exportFilter: any = buildBaseExportFilter('file')
      if (!exportFilter) {
        dispatch(setLoading(false))
        return
      }

      trackDownloadClick(
        clickItem,
        folderRule,
        namingRule,
        undefined,
        folderTitles,
        trackParams
      )

      if (folderRule !== 'customize') {
        folderTitles = []
      } else if (folderTitles.join('') === 'watermark') {
        folderRule = 'watermark'
        folderTitles = []
      } else {
        folderRule = ''
      }

      exportFilter.namePolicy = {
        file: namingRule,
        folder: folderRule,
        folderTitles
      }
      if (folderV2) {
        exportFilter.namePolicy = exportFilter.namePolicy || {}
        exportFilter.namePolicy.folderV2 = folderV2
      }

      if (
        clickItem === 'downloadOnce' &&
        desktopLib.isDesktop &&
        supportsNewDownloadFeature()
      ) {
        dispatch(setLoading(false))
        onClientDownload(
          folderRule,
          namingRule,
          folderTitles,
          clickItem,
          folderV2
        )
        return
      }

      if (clickItem === 'downloadOnce') {
        exportFilter.extra = {
          orderNo: localStorage.getItem('orderNo')
        }
      }

      dispatch(exportPhotos(exportFilter))
        .then((result: any) => {
          const { status } = result
          dispatch(setLoading(false))
          if (status === 0) {
            if (!desktopLib.isDesktop) {
              markClientPromoPending()
            }
            cancelCheckAll()
            dispatch(setExportListDrawerVisible(true))
          }
        })
        .catch(capturePromiseException)
    },
    [
      buildBaseExportFilter,
      dispatch,
      onClientDownload,
      trackDownloadClick,
      cancelCheckAll
    ]
  )

  // 导出处理（区分客户端 / Web）
  const onExport = useCallback(
    (
      folderRule: string,
      namingRule: string[],
      folderTitles: string[],
      clickItem = 'download',
      trackParams: Record<string, unknown>,
      folderV2?: string[]
    ) => {
      if (desktopLib.isDesktop) {
        setFolderModalVisible(false)
        if (clickItem === 'downloadOnce' && !supportsNewDownloadFeature()) {
          onExportWeb(
            folderRule,
            namingRule,
            folderTitles,
            clickItem,
            trackParams,
            folderV2
          )
          return
        }
        onClientDownload(
          folderRule,
          namingRule,
          folderTitles,
          clickItem,
          trackParams,
          folderV2
        )
        return
      }

      onExportWeb(
        folderRule,
        namingRule,
        folderTitles,
        clickItem,
        trackParams,
        folderV2
      )
    },
    [onClientDownload, onExportWeb]
  )

  if (disabled) {
    return (
      <Popover
        content={
          <img style={{ width: '270px' }} src={export_photo_tip_img} alt="" />
        }
        placement="bottom"
      >
        <Button
          type="primary"
          ghost
          disabled
          style={{ width: '66px', marginRight: 10, position: 'relative' }}
        >
          下载
        </Button>
      </Popover>
    )
  }

  return (
    <>
      <Button
        type="primary"
        ghost
        onClick={onDownLoadPictureClick}
        disabled={checkedNum === 0}
        style={{ width: '66px', marginRight: 10, position: 'relative' }}
      >
        下载
      </Button>
      {/* 客户端版本大于1.2.4小于1.3.4时，使用FolderModal，非客户端版本还有其它版本，使用MultiFolderModal */}
      {isVersionBetween('1.3.0', '1.3.4') ? (
        <FolderModal
          visible={folderModalVisible}
          photoTitleList={photoTitleList}
          folderTitleList={folderTitleList}
          onOk={onExport}
          onDownloadOnce={onDownloadOnce}
          onClose={() => {
            setFolderModalVisible(false)
            trackDownloadClick('cancel')
          }}
          disableBatchDownload={firstSelectedPhotoIsLimit}
        />
      ) : (
        <MultiFolderModal
          visible={folderModalVisible}
          photoTitleList={photoTitleList}
          folderTitleList={folderTitleList}
          include={include}
          exclude={exclude}
          onOk={onExport}
          onDownloadOnce={onDownloadOnce}
          onClose={() => {
            setFolderModalVisible(false)
            trackDownloadClick('cancel')
          }}
          disableBatchDownload={firstSelectedPhotoIsLimit}
        />
      )}
    </>
  )
}

export default DownloadButton
